'use client'

import { Check, Sparkles } from 'lucide-react'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'

// Visual mockup of the Link-in-Bio editor as it ships in the app
// dashboard. Two-pane: left config panel, right phone preview. All
// CSS / no real iframe, so it scales cleanly across viewports and
// stays light on the bundle.

const THEMES = ['Light', 'Dark', 'Gradient'] as const
const SAMPLE_TILES = Array.from({ length: 9 }).map((_, i) =>
  unsplashPlaceholderFor(`lib-editor-tile-${i}`, {
    category: i % 3 === 0 ? 'fashion' : i % 3 === 1 ? 'editorial' : 'commerce',
    width: 220,
    height: 260,
  }),
)

export function LinkInBioEditorMockup() {
  return (
    <div className="relative rounded-3xl border bg-card p-3 shadow-sm sm:p-4">
      {/* Browser chrome */}
      <div className="flex items-center justify-between rounded-t-2xl bg-foreground/[0.04] px-3 py-2 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
        </div>
        <span className="font-mono">app.idukki.io/dashboard/link-in-bio</span>
        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-semibold tracking-wider text-emerald-700">LIVE</span>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-b-2xl bg-background/60 p-3 sm:p-4 md:grid-cols-[1fr_300px] md:gap-4">
        {/* Editor pane */}
        <div className="rounded-xl border bg-card p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button type="button" aria-label="back" className="h-6 w-6 rounded-full text-muted-foreground hover:bg-foreground/5">←</button>
              <span className="font-display text-sm font-semibold">Link in Bio</span>
              <span className="rounded-md bg-purple-500/15 px-1.5 py-0.5 text-[8px] font-bold tracking-wider text-purple-700">NEW</span>
            </div>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Create a shoppable page powered by your UGC</p>

          {/* Tabs */}
          <div className="mt-3 flex gap-3 border-b text-[11px] font-semibold">
            {['Brand', 'Social Links', 'Content', 'Publish'].map((t, i) => (
              <span
                key={t}
                className={
                  i === 0
                    ? 'border-b-2 border-foreground pb-1.5 text-foreground'
                    : 'pb-1.5 text-muted-foreground'
                }
              >
                {t}
              </span>
            ))}
          </div>

          {/* Form fields */}
          <div className="mt-3 space-y-3">
            <Field label="Page Title" value="wyo" />
            <Field label="Tagline / Subtitle" value="Shop our latest UGC looks" muted />
            <Field
              label="Logo URL"
              value="https://sk-nz.myshopify.com/cdn/shop/files/logo.png"
              footnote="Auto-filled from your business settings"
              footnoteIcon
            />

            {/* Theme picker */}
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Theme</span>
              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                {THEMES.map((t, i) => (
                  <button
                    key={t}
                    type="button"
                    className={
                      'flex h-12 items-center justify-center rounded-md text-[10px] font-semibold ' +
                      (i === 0
                        ? 'border-2 border-foreground bg-card text-foreground'
                        : i === 1
                          ? 'border bg-foreground text-background'
                          : 'border bg-gradient-to-br from-fuchsia-500 to-violet-500 text-white')
                    }
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Button color + text */}
            <div className="grid grid-cols-[auto_1fr_1fr] items-end gap-2">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Color</span>
                <div className="mt-1.5 h-7 w-7 rounded border bg-foreground" />
              </div>
              <Field label="Hex" value="#353535" compact />
              <Field label="Button Text" value="Shop Now" compact />
            </div>

            {/* Toggles */}
            <div className="space-y-1.5 pt-1">
              <Toggle label="Show Header" on />
              <Toggle label="Show Footer" on />
            </div>

            {/* Save bar */}
            <button
              type="button"
              className="mt-2 w-full rounded-lg bg-foreground py-2 text-[11px] font-semibold text-background"
            >
              Save Link in Bio
            </button>
          </div>
        </div>

        {/* Preview pane */}
        <div className="rounded-xl border bg-gradient-to-b from-primary/[0.04] to-background p-3">
          <div className="mb-2 flex items-center justify-center gap-2 text-[10px] text-muted-foreground">
            Live Preview
            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[8px] font-semibold text-primary">Mobile</span>
          </div>

          {/* Phone */}
          <div className="mx-auto overflow-hidden rounded-[1.4rem] border-[6px] border-foreground/90 bg-card shadow-md">
            <div className="flex items-center justify-between bg-foreground/95 px-2.5 py-1 text-[8px] text-background">
              <span>9:41</span>
              <span>•••</span>
              <span>100%</span>
            </div>
            <div className="bg-card p-2.5">
              <div className="flex flex-col items-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border bg-card font-display text-[11px] font-bold tracking-tight">
                  SK
                </span>
                <p className="mt-1 font-display text-[11px] font-semibold">wyo</p>
                <div className="mt-1 flex gap-2 text-[10px] text-foreground/60">
                  <span>ƒ</span>
                  <span>ɪ</span>
                  <span>in</span>
                </div>
              </div>
              <ul className="mt-2 grid grid-cols-3 gap-1">
                {SAMPLE_TILES.map((src, i) => (
                  <li key={i} className="relative aspect-square overflow-hidden rounded">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-center text-[7px] text-muted-foreground">
                © 2026 Your Brand. All rights reserved.
              </p>
            </div>
          </div>
          <p className="mt-2 text-center font-mono text-[8px] text-muted-foreground">
            https://app.idukki.io/l/brand
          </p>
        </div>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  footnote,
  footnoteIcon,
  muted,
  compact,
}: {
  label: string
  value: string
  footnote?: string
  footnoteIcon?: boolean
  muted?: boolean
  compact?: boolean
}) {
  return (
    <div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div
        className={
          'mt-1 truncate rounded border bg-background px-2 py-1.5 text-[11px] ' +
          (compact ? 'py-1' : '') +
          ' ' +
          (muted ? 'text-muted-foreground' : 'text-foreground')
        }
      >
        {value}
      </div>
      {footnote && (
        <p className="mt-1 flex items-center gap-1 text-[9px] text-muted-foreground">
          {footnoteIcon && <Sparkles className="h-2.5 w-2.5 text-primary" />}
          {footnote}
        </p>
      )}
    </div>
  )
}

function Toggle({ label, on }: { label: string; on?: boolean }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-foreground">{label}</span>
      <span
        className={
          'flex h-4 w-7 items-center rounded-full px-0.5 transition ' +
          (on ? 'bg-emerald-500 justify-end' : 'bg-foreground/20 justify-start')
        }
      >
        <span className="h-3 w-3 rounded-full bg-white">
          {on && <Check className="h-2.5 w-2.5 text-emerald-700" />}
        </span>
      </span>
    </div>
  )
}
